import { useList, useGo, useDelete, useCustomMutation } from '@refinedev/core';
import { useState, useMemo } from 'react';
import {
  FileText,
  Eye,
  Heart,
  MessageCircle,
  Users,
  User,
  Calendar,
  Image as ImageIcon,
  Plus,
  Type,
  Trash2,
  CheckCircle,
  EyeOff,
} from 'lucide-react';
import { notify } from '@/lib/notify';
import { Badge } from '@/components/ui/badge';
import { DataTable, TableToolbar, type Column, type SortState, type BulkAction } from '@/components/tables';
import { FormHeader } from '@/components/forms';
import { usePersistedState } from '@/hooks/use-persisted-state';
import { useScrollRestoration } from '@/hooks/use-scroll-restoration';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import type { Post, ContentStatus } from './types';
import { PostEventsDialog, type EventView } from './PostEventsDialog';

function getStatusVariant(status: ContentStatus): 'default' | 'secondary' | 'destructive' | 'outline' {
  switch (status) {
    case 'PENDING_MODERATION': return 'secondary';
    case 'PUBLISHED': return 'default';
    case 'REJECTED': return 'destructive';
    case 'DRAFT': return 'outline';
    default: return 'secondary';
  }
}

function getStatusLabel(status: ContentStatus): string {
  switch (status) {
    case 'PENDING_MODERATION': return 'На модерации';
    case 'PUBLISHED': return 'Опубликован';
    case 'REJECTED': return 'Отклонён';
    case 'DRAFT': return 'Черновик';
    default: return status;
  }
}

function formatDateShort(dateString: string) {
  return new Date(dateString).toLocaleDateString('ru-RU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function getPostAuthorName(post: Post): string {
  if (post.teacher) { return post.teacher.name; }
  if (post.author) {
    const parts: string[] = [];
    if (post.author.firstName) { parts.push(post.author.firstName); }
    if (post.author.lastName) { parts.push(post.author.lastName); }
    if (parts.length > 0) { return parts.join(' '); }
    if (post.author.username) { return post.author.username; }
  }
  return 'Неизвестный';
}

function getPostAuthorPhoto(post: Post): string | null {
  if (post.teacher) { return post.teacher.photoUrl; }
  if (post.author) { return post.author.photoUrl; }
  return null;
}

function getPostAuthorRole(post: Post): string | null {
  if (post.teacher) { return post.teacher.role; }
  if (post.author) { return 'Пользователь'; }
  return null;
}

function countWords(html: string): number {
  const text = html.replaceAll(/<[^>]*>/g, '').trim();
  if (!text) { return 0; }
  return text.split(/\s+/).length;
}

function getActiveFiltersCount(statusFilter: string): number {
  return statusFilter !== 'all' ? 1 : 0;
}

export function PostsList() {
  useScrollRestoration();
  const go = useGo();
  const [page, setPage] = usePersistedState('posts:page', 1);
  const [sort, setSort] = usePersistedState<SortState | undefined>('posts:sort', {
    field: 'createdAt',
    order: 'desc',
  });
  const [statusFilter, setStatusFilter] = usePersistedState<string>('posts:statusFilter', 'all');
  const [eventsDialog, setEventsDialog] = useState<{ post: Post; type: EventView } | null>(null);
  const [search, setSearch] = usePersistedState('posts:search', '');
  const [deleteItems, setDeleteItems] = useState<Post[]>([]);
  const { mutate: deletePost } = useDelete();
  const { mutate: bulkMutate } = useCustomMutation();
  const pageSize = 20;

  const activeFiltersCount = getActiveFiltersCount(statusFilter);

  const handleResetFilters = () => {
    setStatusFilter('all');
    setPage(1);
  };

  const handleDelete = () => {
    if (deleteItems.length === 0) { return; }
    const count = deleteItems.length;
    for (const item of deleteItems) {
      deletePost({ resource: 'posts', id: item.id, successNotification: false });
    }
    notify.success(`Удалено: ${count}`);
    setDeleteItems([]);
  };

  const queryFilters = useMemo(() => {
    const result = [];
    if (statusFilter !== 'all') {
      result.push({ field: 'status', operator: 'eq' as const, value: statusFilter });
    }
    if (search) {
      result.push({ field: 'search', operator: 'contains' as const, value: search });
    }
    return result;
  }, [statusFilter, search]);

  const listResult = useList<Post>({
    resource: 'posts',
    pagination: { currentPage: page, pageSize, mode: 'server' },
    filters: queryFilters,
    sorters: sort ? [{ field: sort.field, order: sort.order }] : [],
  });

  const data = listResult.result.data ?? [];
  const total = listResult.result.total ?? 0;
  const isLoading = listResult.query.isLoading;

  const columns: Column<Post>[] = [
    {
      key: 'createdAt',
      header: 'Дата',
      width: '180px',
      sortable: true,
      render: (_, post) => (
        <div className="space-y-0.5">
          <div className="flex items-center gap-1.5 text-sm">
            <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
            {formatDateShort(post.createdAt)}
          </div>
          {post.publishedAt && (
            <div className="text-xs text-muted-foreground pl-5">
              Опубл. {formatDateShort(post.publishedAt)}
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'author',
      header: 'Автор',
      width: '200px',
      render: (_, post) => {
        const photoUrl = getPostAuthorPhoto(post);
        const name = getPostAuthorName(post);
        const role = getPostAuthorRole(post);
        return (
          <div className="flex items-center gap-2">
            {photoUrl ? (
              <img
                src={photoUrl}
                alt=""
                className="w-8 h-8 rounded-full object-cover shrink-0"
              />
            ) : (
              <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center shrink-0">
                <User className="h-4 w-4 text-muted-foreground" />
              </div>
            )}
            <div className="flex flex-col min-w-0">
              <span className="font-medium text-sm truncate">{name}</span>
              {role && (
                <span className="text-xs text-muted-foreground truncate">
                  {role}
                </span>
              )}
            </div>
          </div>
        );
      },
    },
    {
      key: 'content',
      header: 'Превью',
      render: (_, post) => {
        const plainText = post.content.replaceAll(/<[^>]*>/g, '');
        const words = countWords(post.content);
        return (
          <div className="max-w-md space-y-1">
            <div className="text-sm line-clamp-2">
              {plainText.slice(0, 150)}
            </div>
            <div className="flex items-center gap-1 text-xs text-muted-foreground">
              <Type className="h-3 w-3" />
              {words} {words === 1 ? 'слово' : (words < 5 ? 'слова' : 'слов')}
            </div>
          </div>
        );
      },
    },
    {
      key: 'images',
      header: 'Фото',
      width: '80px',
      render: (_, post) => (
        <div className="flex items-center gap-1 text-sm text-muted-foreground">
          {post.images && post.images.length > 0 ? (
            <>
              <ImageIcon className="h-3.5 w-3.5" />
              <span>{post.images.length}</span>
            </>
          ) : (
            <span>—</span>
          )}
        </div>
      ),
    },
    {
      key: 'stats',
      header: 'Статистика',
      width: '230px',
      render: (_, post) => (
        <div className="flex items-center gap-3 text-sm text-muted-foreground">
          <button
            type="button"
            className="flex items-center gap-1 cursor-pointer hover:text-foreground transition-colors"
            title="Просмотры"
            onClick={(e) => {
              e.stopPropagation();
              setEventsDialog({ post, type: 'views' });
            }}
          >
            <Eye className="h-3.5 w-3.5" />
            {post.viewsCount}
          </button>
          <button
            type="button"
            className="flex items-center gap-1 cursor-pointer hover:text-foreground transition-colors"
            title="Уникальные просмотры"
            onClick={(e) => {
              e.stopPropagation();
              setEventsDialog({ post, type: 'unique-views' });
            }}
          >
            <Users className="h-3.5 w-3.5" />
            {post.uniqueViewsCount}
          </button>
          <button
            type="button"
            className="flex items-center gap-1 cursor-pointer hover:text-foreground transition-colors"
            title="Лайки"
            onClick={(e) => {
              e.stopPropagation();
              setEventsDialog({ post, type: 'likes' });
            }}
          >
            <Heart className="h-3.5 w-3.5" />
            {post.likesCount}
          </button>
          <button
            type="button"
            className="flex items-center gap-1 cursor-pointer hover:text-foreground transition-colors"
            title="Комментарии"
            onClick={(e) => {
              e.stopPropagation();
              setEventsDialog({ post, type: 'comments' });
            }}
          >
            <MessageCircle className="h-3.5 w-3.5" />
            {post.commentsCount}
          </button>
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Статус',
      width: '140px',
      sortable: true,
      render: (_, post) => (
        <Badge variant={getStatusVariant(post.status)}>
          {getStatusLabel(post.status)}
        </Badge>
      ),
    },
  ];

  const handleBulkStatusChange = (items: Post[], action: 'publish' | 'unpublish') => {
    const ids = items.map((p) => p.id);
    bulkMutate(
      {
        url: '/admin/posts/bulk',
        method: 'post',
        values: { ids, action },
      },
      {
        onSuccess: () => {
          notify.success(action === 'publish' ? 'Посты опубликованы' : 'Посты сняты с публикации');
          listResult.query.refetch();
        },
        onError: () => {
          notify.error('Не удалось выполнить операцию');
        },
      },
    );
  };

  const bulkActions: BulkAction<Post>[] = [
    {
      label: 'Опубликовать',
      icon: <CheckCircle className="h-4 w-4" />,
      onClick: (items) => handleBulkStatusChange(items, 'publish'),
      showOnlyWhenSelected: true,
    },
    {
      label: 'Снять с публикации',
      icon: <EyeOff className="h-4 w-4" />,
      onClick: (items) => handleBulkStatusChange(items, 'unpublish'),
      showOnlyWhenSelected: true,
    },
    {
      label: 'Просмотр',
      icon: <Eye className="h-4 w-4" />,
      onClick: (items) => {
        if (items.length === 1) {
          go({ to: `/posts/show/${items[0].id}` });
        }
      },
      showOnlyWhenSelected: true,
    },
    {
      label: 'Удалить',
      icon: <Trash2 className="h-4 w-4" />,
      onClick: setDeleteItems,
      variant: 'destructive',
      showOnlyWhenSelected: true,
    },
  ];

  return (
    <div className="space-y-6">
      <FormHeader
        title="Посты"
        description="Управление пользовательскими публикациями"
        sticky
        icon={<FileText className="h-5 w-5" />}
        actions={
          <Button onClick={() => go({ to: '/posts/create' })}>
            <Plus className="h-4 w-4 mr-2" />
            Создать пост
          </Button>
        }
      />

      <TableToolbar
        searchValue={search}
        onSearchChange={(v) => {
          setSearch(v);
          setPage(1);
        }}
        searchPlaceholder="Поиск по контенту..."
        filters={
          <div className="grid gap-4">
            <div className="grid gap-2">
              <Label htmlFor="status-filter">Статус</Label>
              <Select
                value={statusFilter}
                onValueChange={(v) => {
                  setStatusFilter(v);
                  setPage(1);
                }}
              >
                <SelectTrigger id="status-filter">
                  <SelectValue placeholder="Статус" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Все статусы</SelectItem>
                  <SelectItem value="PUBLISHED">Опубликованы</SelectItem>
                  <SelectItem value="PENDING_MODERATION">На модерации</SelectItem>
                  <SelectItem value="DRAFT">Черновики</SelectItem>
                  <SelectItem value="REJECTED">Отклонены</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        }
        filtersCount={activeFiltersCount}
        onResetFilters={handleResetFilters}
      />

      <DataTable
        columns={columns}
        data={data}
        total={total}
        page={page}
        pageSize={pageSize}
        isLoading={isLoading}
        onPageChange={setPage}
        onRowClick={(post) => go({ to: `/posts/edit/${post.id}` })}
        emptyMessage="Нет постов"
        selectable
        rowKey="id"
        sort={sort}
        onSortChange={setSort}
        bulkActions={bulkActions}
      />

      <PostEventsDialog
        post={eventsDialog?.post ?? null}
        type={eventsDialog?.type ?? null}
        open={eventsDialog !== null}
        onOpenChange={(open) => {
          if (!open) { setEventsDialog(null); }
        }}
      />

      <AlertDialog open={deleteItems.length > 0} onOpenChange={() => setDeleteItems([])}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {deleteItems.length === 1
                ? 'Удалить пост?'
                : `Удалить ${deleteItems.length} постов?`}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {deleteItems.length === 1 ? (
                <>Вы уверены, что хотите удалить этот пост? Это действие нельзя отменить.</>
              ) : (
                <>Вы уверены, что хотите удалить {deleteItems.length} постов? Это действие нельзя отменить.</>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Отмена</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Удалить
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
